import { makeAutoObservable } from 'mobx';
import _ from 'lodash';
import { $definitions, } from 'ziphy-web-shared/basic/entities/notes/definitions';
export class SectionStore {
    constructor({ parent }) {
        this.parent = parent;
        this.data = {};
        makeAutoObservable(this);
    }
    // Computed
    get encounterId() {
        return this.parent.encounter.id;
    }
    get id() {
        return this.data.sectionId;
    }
    get title() {
        return this.data.title;
    }
    get freeText() {
        return this.data.freeText;
    }
    get required() {
        return this.data.required;
    }
    get isMayHaveChildren() {
        return this.id === 'assessment' || this.id === 'billing';
    }
    get plusWithPlaceholder() {
        return this.id !== 'billing';
    }
    get allBlocks() {
        return this.getBlocks(this.data.content, null);
    }
    get allFlattenBlocksMap() {
        const map = new Map();
        function getFlatten(nodes) {
            var _a;
            for (const node of nodes) {
                map.set(node.block.ref, node);
                ((_a = node.blocks) === null || _a === void 0 ? void 0 : _a.length) && getFlatten(node.blocks);
            }
        }
        getFlatten(this.allBlocks);
        return map;
    }
    get blocks() {
        if (this.id === 'assessment') {
            return this.allBlocks.filter((x) => !x.block.isResource || x.block.isCondition);
        }
        return this.allBlocks;
    }
    get unresolvedBlocks() {
        if (this.id === 'assessment') {
            return this.allBlocks.filter((x) => x.block.isResource && !x.block.isCondition);
        }
        return [];
    }
    get macros() {
        var _a;
        const macrosList = Object.entries(((_a = $definitions.macros.groupedBySection) === null || _a === void 0 ? void 0 : _a[this.id]) || {});
        let rootResources = [];
        const result = {
            plus: macrosList,
            text: macrosList,
            //
            plusSecondary: macrosList.filter(([type]) => type === 'resource'),
            textSecondary: [],
        };
        const filterList = (list, filter, resources) => {
            return list.map(([type, value]) => {
                if (type === 'resource') {
                    value = value.filter((x) => {
                        const resourceType = x.properties.resourceType;
                        if (filter === 'root') {
                            return resources.includes(resourceType);
                        }
                        else if (filter === 'root_billing') {
                            return (resources.includes(resourceType) &&
                                (this.practiceSettings.cptSearchLimit ? !x.id.startsWith("billing_filter") : true));
                        }
                        else {
                            return !resources.includes(resourceType);
                        }
                    });
                }
                return [type, value];
            });
        };
        if (this.id === 'assessment') {
            rootResources = ['Condition'];
            return {
                plus: result.plus,
                text: result.text,
                //
                plusSecondary: filterList(result.plusSecondary, undefined, rootResources),
                textSecondary: filterList(result.textSecondary, undefined, rootResources),
            };
        }
        if (this.id === 'billing') {
            rootResources = ['Procedure'];
            return {
                plus: filterList(macrosList, 'root_billing', rootResources),
                text: result.text,
                //
                plusSecondary: filterList(result.plusSecondary, undefined, rootResources),
                textSecondary: filterList(result.textSecondary, undefined, rootResources),
            };
        }
        return result;
    }
    get usedUniqueBlocks() {
        return new Map([...this.allFlattenBlocksMap].filter(([ref]) => {
            return this.parent.uniqueBlocksData.has(ref);
        }));
    }
    get errors() {
        return _.pick(this.parent.errors, ['empty_section']);
    }
    get practiceSettings() {
        return this.parent.practiceSettings;
    }
    // Mutations
    UPDATE_DATA(value) {
        this.data = value;
    }
    // Actions
    validate() {
        let isValid = !this.required;
        Array.from(this.allFlattenBlocksMap.values()).forEach((x) => {
            x.block.validate();
            if (this.required && !x.block.isEmptyTextBlock)
                isValid = true;
        });
        this.parent.SET_ERROR('empty_section', { sectionId: this.id }, !isValid);
    }
    getBlocks(items, parentRef, deep = 0) {
        let blocks = items.map((x, index) => {
            var _a;
            const block = this.parent.getBlock(this.id, x.ref);
            /*
             * todo: KosTbIJlb detected
             *
             *       ||
             *       ||
             *       ||
             *      _||_
             *     |____|
             *     | || |
             *     | || |
             *     | || |
             *     | || |
             *     | || |
             *     |_||_|
             *      |__|
             *     /____\
             *
             * */
            let shouldHaveEmptyChildBlock = false;
            if (block.isResource && this.freeText) {
                shouldHaveEmptyChildBlock =
                    (this.id === 'assessment' && block.isCondition) ||
                        (this.id === 'billing' &&
                            block.resourceType === 'Procedure' &&
                            this.practiceSettings.billingReasonLimit > 0);
            }
            return {
                parentRef: parentRef,
                block: block,
                blocks: ((_a = x.content) === null || _a === void 0 ? void 0 : _a.length)
                    ? this.getBlocks(x.content, x.ref, deep + 1)
                    : shouldHaveEmptyChildBlock
                        ? [
                            {
                                parentRef: block.ref,
                                block: this.parent.getEmptyBlock(this.id, block.ref, deep + 1),
                                blocks: [],
                                position: 0,
                            },
                        ]
                        : [],
                position: index,
            };
        });
        let emptyBlockInsertIndex = blocks.length;
        if (this.id === 'assessment') {
            const texts = blocks.filter((item) => !item.block.isResource);
            const resources = blocks.filter((item) => item.block.isResource);
            blocks = [...texts, ...resources];
            emptyBlockInsertIndex = texts.length;
        }
        if (this.freeText) {
            blocks.splice(emptyBlockInsertIndex, 0, {
                parentRef: parentRef,
                block: this.parent.getEmptyBlock(this.id, parentRef, deep),
                blocks: [],
                position: blocks.length,
            });
        }
        return blocks;
    }
}
